import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete, Button, TextField, Checkbox, createFilterOptions,
} from '@mui/material'
import { toast } from 'react-toastify'
import {
  getGrupos, registrarPrecioGrupo, importarCatalogoSap, getGrupoMateriales,
  registrarCambioMasivo, getPlanoRechazados, getGrupoMargenes,
} from 'src/services/preciosService'
import TablaPrecios from './TablaPrecios'
import MoneyField from './MoneyField'
import MargenTarjetas from './MargenTarjetas'
import { fmt, pct, columnasBase, PuntoColor, chip } from './columnasPrecios'

const mesActual = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Ahora, en el formato que come un input datetime-local: 'YYYY-MM-DDTHH:mm'. */
const ahoraLocalISO = () => {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Legible para el usuario: '31/08/2026 18:00'. Sin hora si es medianoche. */
const legible = (v) => {
  if (!v) return ''
  const [fecha, hora] = String(v).split('T')
  const [a, m, d] = fecha.split('-')
  const conHora = hora && hora !== '00:00' ? ` ${hora}` : ''
  return `${d}/${m}/${a}${conHora}`
}

/** Días desde hoy hasta una fecha 'YYYY-MM-DD' o 'YYYY-MM-DDTHH:mm'. Negativo o 0 = ya rige. */
const diasHasta = (iso) => {
  if (!iso) return 0
  const [a, m, d] = String(iso).split('T')[0].split('-').map(Number)
  const destino = new Date(a, (m || 1) - 1, d || 1)
  const hoy = new Date()
  hoy.setHours(0, 0, 0, 0)
  return Math.round((destino - hoy) / 86400000)
}

/**
 * El buscador tiene que encontrar la moto por el código del material, no solo por el nombre del modelo:
 * el correo del comité a veces trae el código y nada más.
 */
const filtro = createFilterOptions({
  stringify: (o) => `${o.marca || ''} ${o.modelo} ${o.anio_modelo || ''} ${o.conca || ''} ${o.materiales_texto || ''}`,
  limit: 60,
})

/**
 * Trade fija el precio por MODELO, no por color.
 * Arranque de la cascada: un PVP + un %IVA -> se abre el plano de cada color -> Back lo arma y lo publica.
 * OJO (F27): guardar acá deja el precio PROPUESTO. El precio queda puesto cuando Back publica.
 */
const TradePorModelo = ({ onChanged }) => {
  const [grupos, setGrupos] = useState([])
  // La selección es una LISTA: un precio de lista suele salir igual para varias motos, y hasta ahora
  // había que repetir la operación una por una.
  //
  // `sel` queda como valor DERIVADO, no como estado aparte. Con exactamente una moto elegida vale
  // esa moto y toda la pantalla se comporta igual que siempre; con ninguna o con varias vale null y
  // los paneles que dependen de una sola —colores, simulación de márgenes, resumen del modelo— se
  // ocultan solos, porque ya estaban escritos como `sel && (...)`. Así el multi-select no obliga a
  // reescribir esas tres secciones ni arriesga cambiarles el comportamiento.
  const [sels, setSels] = useState([])
  const sel = sels.length === 1 ? sels[0] : null
  const [pvp, setPvp] = useState('')
  const [iva, setIva] = useState('0.27')
  const [mes, setMes] = useState(mesActual())
  // F37: desde cuándo rige. Default: ahora = el comportamiento de siempre.
  // F39: y hasta cuándo. Vacío = no vence, que es el caso normal de un precio de lista.
  const [desde, setDesde] = useState(ahoraLocalISO())
  const [hasta, setHasta] = useState('')
  const diasParaArrancar = diasHasta(desde)
  const esProgramado = diasParaArrancar > 0
  const esTemporal = Boolean(hasta)
  // el rango al revés no se manda: se avisa antes de tocar el botón
  const rangoInvertido = esTemporal && hasta <= desde
  const [colores, setColores] = useState([])
  const [seleccion, setSeleccion] = useState([])
  const [devueltos, setDevueltos] = useState([])
  const [margenes, setMargenes] = useState([])
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  /**
   * Si la consulta falla (típico: el back reiniciándose) hay que DECIRLO. Antes se dejaba la tabla
   * vacía en silencio y parecía que no había modelos.
   */
  const fetchGrupos = async () => {
    setIsLoading(true)
    const r = await getGrupos()
    if (!r || !r.data) {
      setError('No se pudo consultar el catálogo de modelos. Puede que el servidor esté reiniciando.')
      setGrupos([])
    } else {
      setError('')
      setGrupos(r.data)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    fetchGrupos()
    getPlanoRechazados().then((r) => setDevueltos((r && r.data) || []))
  }, [])

  // Se traen los colores de TODAS las motos elegidas, no solo de una: el precio se puede aplicar a
  // los productos que se marquen, y para marcarlos hay que verlos.
  useEffect(() => {
    if (!sels.length) {
      setColores([])
      setSeleccion([])
      return undefined
    }
    // Con UNA sola moto se precargan su precio e IVA actuales: se está ACTUALIZANDO, no creando.
    // Con varias no se precarga nada, porque no existe un "precio actual" común a todas y poner el
    // de una sería sugerir un número que no vale para el resto.
    if (sels.length === 1) {
      const g = sels[0]
      const base = Number(g.pvp_propuesto) > 0 ? g.pvp_propuesto : g.pvp
      setPvp(Number(base) > 0 ? String(base) : '')
      setIva(String(Number(g.iva_pct || 0.27)))
    }
    let vivo = true
    Promise.all(
      sels.map((g) => getGrupoMateriales(g.id).then((r) => ({ g, cs: (r && r.data) || [] }))),
    ).then((res) => {
      if (!vivo) return
      const todos = res.flatMap(({ g, cs }) =>
        cs.map((c) => ({
          ...c,
          grupo_id: g.id,
          grupo_label: `${g.marca || ''} ${g.modelo} ${g.anio_modelo || ''}`.trim(),
        })),
      )
      setColores(todos)
      // por defecto TODOS: el caso normal es que el lote entero comparta precio
      setSeleccion(todos.map((c) => c.material))
    })
    return () => {
      vivo = false
    }
  }, [sels])

  /**
   * Los márgenes se recalculan contra el PVP que se está tecleando: no hay que guardar para ver a
   * cuánto le queda facturada la moto a cada cliente. El margen se decide con el precio, no aparte.
   */
  const cargarMargenes = async (grupoId, pvpSim, ivaSim) => {
    const r = await getGrupoMargenes(grupoId, { pvp: pvpSim || 0, ivaPct: ivaSim || 0 })
    setMargenes((r && r.data) || [])
  }

  useEffect(() => {
    if (!sel) {
      setMargenes([])
      return undefined
    }
    const valor = Number(String(pvp).replace(/\D/g, ''))
    // se espera a que deje de teclear: cada simulación es una consulta
    const t = setTimeout(() => cargarMargenes(sel.id, valor, iva), 400)
    return () => clearTimeout(t)
  }, [sel, pvp, iva])

  // `guardarMargenLista` se eliminó: este bloque pasó a SOLO LECTURA.
  // El margen se escribe únicamente desde "Márgenes motocicletas".

  /**
   * ¿Esta moto tiene REGLAS de margen de verdad, o el bloque mostraría puros ceros?
   *
   * Una moto nueva todavía no existe como producto: no tiene excepción propia, ni regla de su marca,
   * ni nada. El resolvedor devuelve 0 y `origen` queda en `sin_regla`, así que el panel llenaba la
   * pantalla de tarjetas al 0 % que se leen como "esta moto se factura regalada". Eso es ruido, no
   * información: se esconde el bloque entero y se dice por qué.
   *
   * Las listas que no llevan margen (base / pvp / fijo) no cuentan: esas facturan igual sin regla.
   */
  const conReglaDeMargen = margenes.filter(
    (l) => Number(l.aplica) === 1
      && !['base', 'pvp', 'fijo'].includes(l.formula)
      && l.origen !== 'sin_regla',
  )
  const tieneReglas = conReglaDeMargen.length > 0

  /**
   * Lo que se dibuja: las clasificaciones que SÍ tienen un porcentaje de regla, más las que por
   * fórmula no llevan margen (base / pvp / fijo), que facturan igual sin regla. Una que lleva margen
   * pero no tiene regla para esta moto se omite: mostrarla al 0 % es afirmar un dato que no existe.
   */
  const tarjetasMargen = margenes.filter((l) => Number(l.aplica) === 1 && (
    ['base', 'pvp', 'fijo'].includes(l.formula)
    // un 0 % cargado es una decisión y se muestra; lo que se esconde es el 0 de `sin_regla`
    || l.origen !== 'sin_regla'
  ))

  const todosSeleccionados = colores.length > 0 && seleccion.length === colores.length

  const toggleColor = (material) =>
    setSeleccion((prev) =>
      prev.includes(material) ? prev.filter((m) => m !== material) : [...prev, material],
    )

  /**
   * Con TODOS los colores marcados el precio se propone en el modelo (el camino sano: el conca queda
   * como fuente y ningún color puede quedarse atrás). Con una selección parcial se guarda solo a esos
   * códigos — sirve para casos puntuales, pero deja al modelo descuadrado y se avisa.
   */
  const guardar = async () => {
    if (!sels.length) {
      toast.warn('Elige al menos un modelo')
      return
    }
    const valor = Number(String(pvp).replace(/\D/g, ''))
    if (!valor) {
      toast.warn('Ingresa el PVP')
      return
    }
    if (rangoInvertido) {
      toast.warn('La fecha de vencimiento tiene que ser posterior al inicio')
      return
    }

    if (!seleccion.length) {
      toast.warn('No hay productos seleccionados')
      return
    }

    if (todosSeleccionados) {
      // Están marcados TODOS los productos de todas las motos elegidas: se propone por modelo, que
      // es el camino sano — el conca queda como fuente y ningún color puede quedarse atrás.
      // Se registra de a un modelo porque el procedimiento trabaja por grupo. Si alguno falla, los
      // otros ya quedaron registrados: se informa cuáles no entraron en vez de fingir todo-o-nada.
      const fallaron = []
      let ok = 0
      let tocados = 0
      let programados = 0
      let temporales = 0
      let ciclo = mes
      // Secuencial a proposito: el procedimiento trabaja por grupo y lanzarlos en paralelo
      // mezclaria los ciclos. Un `for...of` con `await` es la forma directa de escribirlo;
      // pasarlo a `reduce` para satisfacer la regla lo haria menos legible, no mas correcto.
      // eslint-disable-next-line no-restricted-syntax
      for (const g of sels) {
        // eslint-disable-next-line no-await-in-loop
        const r = await registrarPrecioGrupo(g.id, valor, iva, mes, desde, hasta)
        const d = (r && r.data && r.data[0]) || null
        if (!d || d.error) {
          // El motivo lo da el procedimiento ('Conca inexistente o PVP invalido', 'La fecha hasta
          // tiene que ser posterior...'). Se arrastra hasta el toast: tragárselo y mostrar solo
          // "no se pudo" obliga a adivinar, que es justo lo que no se puede hacer con un precio.
          const motivo = (d && d.error) || (r === false ? 'la petición falló' : 'respuesta vacía del servidor')
          fallaron.push(`${`${g.modelo} ${g.anio_modelo || ''}`.trim()}: ${motivo}`)
          // eslint-disable-next-line no-continue
          continue
        }
        ok += 1
        tocados += Number(d.materiales) || 0
        if (Number(d.es_programado) === 1) programados += 1
        if (Number(d.es_temporal) === 1) temporales += 1
        if (d.mes) ciclo = d.mes
      }

      if (!ok) {
        toast.error(fallaron.length ? fallaron.join(' · ') : 'No se pudo registrar el precio')
        return
      }

      const donde = sels.length === 1
        ? `${sels[0].modelo} ${sels[0].anio_modelo || ''}`.trim()
        : `${ok} modelo(s)`
      const cuantos = `${tocados} producto(s) a ${fmt(valor)}`
      const vence = temporales > 0 ? ` Vence el ${legible(hasta)}.` : ''
      toast.success(
        programados > 0
          ? `${donde}: ${cuantos} PROGRAMADOS para el ${legible(desde)}. El precio de hoy no se movió.${vence} Falta que Back Comercial lo publique.`
          : `${donde}: ${cuantos} propuestos · ciclo ${ciclo}.${vence} Queda pendiente que Back Comercial lo publique.`,
      )
      if (fallaron.length) {
        toast.error(`No se pudo registrar en: ${fallaron.join(' · ')}`)
      }
    } else {
      // Selección parcial: el precio va SOLO a los productos marcados, aunque sean de varias motos.
      // Deja al modelo descuadrado a propósito y por eso se avisa con un warning, no con un success.
      const items = seleccion.map((material) => ({ material, pvp: String(valor) }))
      const r = await registrarCambioMasivo(items)
      if (!r || !r.success) {
        toast.error('No se pudo registrar el precio de los productos seleccionados')
        return
      }
      const restantes = colores.length - seleccion.length
      toast.warn(
        `Precio aplicado a ${seleccion.length} de ${colores.length} producto(s). Los otros ${restantes} mantienen su precio anterior, así que ${sels.length > 1 ? 'esos modelos quedan' : 'el modelo queda'} descuadrado.`,
      )
    }

    // Se recargan los productos de todas las motos elegidas, no solo de una.
    const res = await Promise.all(
      sels.map((g) => getGrupoMateriales(g.id).then((r) => ({ g, cs: (r && r.data) || [] }))),
    )
    setColores(
      res.flatMap(({ g, cs }) =>
        cs.map((c) => ({
          ...c,
          grupo_id: g.id,
          grupo_label: `${g.marca || ''} ${g.modelo} ${g.anio_modelo || ''}`.trim(),
        })),
      ),
    )
    await fetchGrupos()
    onChanged?.()
  }

  /**
   * Traer el catálogo de SAP a demanda. Normalmente NO hace falta: corre a diario por Cloud Scheduler
   * y ahí mismo se crean los modelos nuevos, se vinculan los colores y un color nuevo hereda el precio
   * de su modelo. Este botón queda solo para no tener que esperar al otro día en pruebas.
   */
  const importar = async () => {
    const r = await importarCatalogoSap()
    if (!r || r.ok === false) {
      toast.error((r && r.error) || 'No se pudo importar el catálogo')
      return
    }
    const c = r.conca || {}
    toast.success(
      `Catálogo actualizado: ${r.nuevos || 0} materiales nuevos, ${c.grupos_nuevos || 0} modelos nuevos.`,
    )
    fetchGrupos()
  }

  const opciones = useMemo(
    () =>
      grupos.map((g) => ({
        ...g,
        etiqueta: `${g.marca || ''} ${g.modelo} ${g.anio_modelo || ''} · ${g.materiales} color(es)`.trim(),
      })),
    [grupos],
  )

  // Mismo orden en todas las tablas del módulo: material, marca, modelo, año, color, zona, PVP, IVA
  const columns = useMemo(
    () => [
      ...columnasBase(['material', 'marca', 'modelo', 'anio', 'color', 'zona', 'pvp', 'sap', 'iva'], {
        color: { header: 'Colores' },
        pvp: { header: 'PVP del modelo' },
      }),
      {
        id: 'alertas',
        header: 'Revisar',
        size: 200,
        accessorFn: (r) =>
          Number(r.materiales_sin_precio || 0) + Number(r.materiales_descuadrados || 0)
          + Number(r.planos_rechazados || 0),
        Cell: ({ row }) => {
          const sin = Number(row.original.materiales_sin_precio || 0)
          const desc = Number(row.original.materiales_descuadrados || 0)
          const rech = Number(row.original.planos_rechazados || 0)
          const abiertos = Number(row.original.planos_abiertos || 0)
          if (!sin && !desc && !rech && !abiertos) return <span style={{ color: '#9aa5b1' }}>—</span>
          return (
            <span style={{ fontSize: 11.5, display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              {rech > 0 && <span style={{ color: '#b3261e', fontWeight: 700 }}>{rech} devuelto(s)</span>}
              {abiertos > 0 && <span style={{ color: '#1c4a80' }}>{abiertos} sin publicar</span>}
              {sin > 0 && <span style={{ color: '#d64545' }}>{sin} sin precio</span>}
              {desc > 0 && <span style={{ color: '#b26a00' }}>{desc} descuadrado(s)</span>}
            </span>
          )
        },
      },
    ],
    [],
  )

  return (
    <div>
      {/* Lo que Back devolvió: es lo primero que Trade tiene que resolver */}
      {devueltos.length > 0 && (
        <div
          style={{
            background: '#fdecec',
            border: '1px solid #f3b6b6',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 14,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 13, color: '#8f2d2d', marginBottom: 6 }}>
            Back Comercial te devolvió {devueltos.length} plano(s)
          </div>
          {devueltos.slice(0, 5).map((d) => (
            <div key={d.id} style={{ fontSize: 12.5, color: '#7a3030', marginBottom: 3 }}>
              <b>{d.material}</b> · {d.marca} {d.modelo_sap} {d.color_sap} — {d.motivo_rechazo}
              <span style={{ color: '#9c6b6b' }}> ({d.rechazado_por}, {d.fecha_rechazo})</span>
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: '#9c6b6b', marginTop: 4 }}>
            El precio nunca se aplicó: corrige la propuesta y vuelve a guardarla.
          </div>
        </div>
      )}

      <div className="precios-card">
        <div className="precios-section-title">Actualizar precio de un modelo</div>

        {/* Tres grupos rotulados en rejilla, en vez de una sola fila que se partia donde cayera:
            que modelos, cuanto vale y desde cuando rige. Las columnas quedan alineadas entre grupos
            y el orden de lectura es el del flujo, no el del ancho de cada campo. */}
        <div className="precios-form-grupo">
          <div className="precios-form-rotulo"><span className="n">1</span>Qué modelos</div>
          <div className="precios-grid precios-grid-1">
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={opciones}
              value={sels}
              onChange={(e, v) => setSels(v)}
              filterOptions={filtro}
              getOptionLabel={(o) => o.etiqueta || ''}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              size="small"
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13 }}>{o.etiqueta}</span>
                    <span style={{ fontSize: 11, color: '#8a94a0' }}>{o.materiales_texto}</span>
                  </span>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Modelos, año o código de material"
                  placeholder={sels.length ? '' : 'Puedes elegir varios. Ej: BLACK 2023 o 61001403'}
                />
              )}
            />
          </div>
        </div>

        <div className="precios-form-grupo">
          <div className="precios-form-rotulo"><span className="n">2</span>Precio</div>
          <div className="precios-grid precios-grid-2">
            <MoneyField label="Nuevo PVP" value={pvp} onChange={setPvp} />
            <TextField
              select
              size="small"
              label="% IVA"
              value={iva}
              onChange={(e) => setIva(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="0.19">19% (IVA)</option>
              <option value="0.27">27% (IVA + impo. consumo)</option>
            </TextField>
          </div>
        </div>

        <div className="precios-form-grupo">
          <div className="precios-form-rotulo"><span className="n">3</span>Vigencia</div>
          <div className="precios-grid precios-grid-3">
            <TextField
              size="small"
              label="Ciclo (mes)"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
            />
            {/* F37: el precio puede quedar PROGRAMADO. "dejarlo programado que inicie desde mañana,
                porque yo mañana obviamente no me voy a conectar a cambiar un precio" */}
            <TextField
              size="small"
              type="datetime-local"
              label="Empieza a regir"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {/* F39: el fin. Las dos fechas viajan al plano (columnas D y C); a SAP le llega la fecha
                —LSMW no toma hora— y la hora la usan los canales con API. */}
            <TextField
              size="small"
              type="datetime-local"
              label="Vence"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={rangoInvertido}
              helperText={rangoInvertido ? 'Tiene que ser posterior al inicio' : 'Vacío = no vence'}
            />
          </div>
        </div>

        {(esProgramado || esTemporal) && (
          <div style={{
            marginTop: 8, fontSize: 12.5, padding: '6px 10px', borderRadius: 4,
            background: '#eef6ff', border: '1px solid #cfe2f7', color: '#0f2439',
          }}>
            {esProgramado && (
              <>
                Queda <b>programado</b> para el <b>{legible(desde)}</b>
                {diasParaArrancar === 1 ? ' (mañana)' : ` (en ${diasParaArrancar} día(s))`}.
                {' '}El precio de hoy <b>no se mueve</b> hasta esa fecha.
              </>
            )}
            {esProgramado && esTemporal && <br />}
            {esTemporal && (
              <>
                <b>Vence el {legible(hasta)}</b>; después vuelve a regir el precio anterior.
              </>
            )}
          </div>
        )}

        {/* Varias motos: se dice explícitamente qué va a pasar, porque los paneles que lo mostrarían
            —la simulación de márgenes— no aparece en modo lote. */}
        {sels.length > 1 && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: '#6b7684' }}>
            Mismo PVP para <b>{sels.length} modelos</b>. Desmarca abajo los productos que no deban recibirlo.
            <div style={{ marginTop: 4 }}>
              La simulación de márgenes es por moto: no se muestra en lote.
            </div>
            {sels.some((g) => Number(g.pvp_propuesto) > 0 && Number(g.pvp_propuesto) !== Number(g.pvp)) && (
              <div style={{ marginTop: 4, color: '#b26a00' }}>
                Ojo: alguna de las elegidas ya tiene una propuesta sin publicar, y esta la reemplaza.
              </div>
            )}
          </div>
        )}

        {sel && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: '#6b7684' }}>
            {Number(sel.pvp) > 0 ? (
              <>
                Precio vigente: <b>{fmt(sel.pvp)}</b> con IVA {pct(sel.iva_pct)}.
              </>
            ) : (
              <>Sin precio publicado.</>
            )}{' '}
            {Number(sel.pvp_sap) > 0 && (
              <>
                SAP hoy tiene <b>{fmt(sel.pvp_sap)}</b>.{' '}
              </>
            )}
            {Number(sel.pvp_propuesto) > 0 && Number(sel.pvp_propuesto) !== Number(sel.pvp) && (
              <span style={{ color: '#b26a00' }}>
                Ya hay una propuesta de <b>{fmt(sel.pvp_propuesto)}</b> sin publicar.{' '}
              </span>
            )}
            Afecta <b>{sel.materiales} color(es)</b>; abre un plano por color.
          </div>
        )}

        {/* Los productos que cubre la selección: se ve a QUIÉNES les cambia el precio antes de guardar.
            Con varias motos elegidas se listan los de todas, cada uno rotulado con su modelo. */}
        {colores.length > 0 && (
          <div style={{ marginTop: 16, borderTop: '1px solid #e3e7ec', paddingTop: 14 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#0f2439' }}>
                {sels.length > 1
                  ? `Productos de los ${sels.length} modelos`
                  : 'Colores de este modelo'}{' '}
                — {seleccion.length} de {colores.length} seleccionados
              </div>
              <Button
                size="small"
                variant="text"
                onClick={() => setSeleccion(todosSeleccionados ? [] : colores.map((c) => c.material))}
              >
                {todosSeleccionados ? 'Quitar todos' : 'Seleccionar todos'}
              </Button>
            </div>
            <div style={{ fontSize: 12, color: '#6b7684', margin: '4px 0 10px' }}>
              Con todos marcados, el precio va al modelo. Si desmarcas alguno, el modelo queda descuadrado.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {colores.map((c) => {
                const desc = Number(c.descuadrado) === 1
                const sin = Number(c.sin_precio) === 1
                const marcado = seleccion.includes(c.material)
                const dif = Number(c.diferencia || 0)
                // el estado manda el color de la tarjeta: rojo sin precio, ámbar descuadrado, verde al día
                const tono = sin
                  ? { barra: '#d64545', fondo: '#fdecec', borde: '#f3b6b6', texto: '#8f2d2d' }
                  : desc
                    ? { barra: '#e0a300', fondo: '#fff8e8', borde: '#f0d089', texto: '#8a5a00' }
                    : { barra: '#1f9d55', fondo: '#f2fbf5', borde: '#c5e8d2', texto: '#166534' }
                return (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleColor(c.material)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') toggleColor(c.material)
                    }}
                    title={`${c.material} · ${c.color || 'sin color'} · ${fmt(c.pvp_actual)}`}
                    style={{
                      borderLeft: `4px solid ${tono.barra}`,
                      border: `1px solid ${marcado ? tono.barra : '#e3e7ec'}`,
                      borderLeftWidth: 4,
                      borderLeftColor: tono.barra,
                      boxShadow: marcado ? `0 0 0 2px ${tono.barra}33` : 'none',
                      background: marcado ? tono.fondo : '#fff',
                      opacity: marcado ? 1 : 0.6,
                      borderRadius: 8,
                      padding: '6px 12px 8px',
                      minWidth: 196,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Checkbox size="small" checked={marcado} sx={{ p: 0.5 }} readOnly />
                      <PuntoColor nombre={c.color} size={14} />
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2439' }}>
                        {c.color || <span style={{ color: '#9aa5b1' }}>sin color</span>}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7684', marginLeft: 30, fontVariantNumeric: 'tabular-nums' }}>
                      {c.material}
                    </div>
                    {/* Con varias motos elegidas el color solo no alcanza para saber cuál es: se rotula
                        cada tarjeta con su modelo. Con una sola sobra, porque ya está arriba. */}
                    {sels.length > 1 && c.grupo_label && (
                      <div style={{ fontSize: 10.5, color: '#8a94a0', marginLeft: 30 }}>
                        {c.grupo_label}
                      </div>
                    )}
                    <div style={{ fontSize: 12.5, marginTop: 3, marginLeft: 30 }}>
                      {sin ? (
                        <span style={{ color: tono.texto, fontWeight: 700 }}>sin precio</span>
                      ) : (
                        <span style={{ color: tono.texto, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(c.pvp_actual)}
                        </span>
                      )}
                      {desc && !sin && (
                        <div style={{ fontSize: 11, color: tono.texto }}>
                          {dif > 0 ? '+' : ''}{fmt(dif)} vs el modelo
                        </div>
                      )}
                      {!desc && !sin && (
                        <div style={{ fontSize: 11, color: '#7d8791' }}>igual al modelo</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* SOLO LECTURA a propósito. Acá se VE el efecto del precio; el margen NO se edita.
            Antes se editaba en línea desde este mismo bloque, y eso mezclaba dos decisiones con dueños
            distintos: el PVP lo pone Trade, el margen es política comercial. Además el editor mandaba
            SIEMPRE el campo de margen, así que guardar podía pisar en silencio una excepción vigente
            (la regla R1: margen omitido significa "dejá el que está", no "volvé al global").
            El margen se administra en "Márgenes motocicletas". */}
        {/* Moto sin ninguna regla de margen: no se dibujan tarjetas al 0 %, se explica el estado. */}
        {sel && margenes.length > 0 && !tieneReglas && (
          <div
            style={{
              marginTop: 16,
              borderTop: '1px solid #e3e7ec',
              paddingTop: 14,
              fontSize: 12.5,
              color: '#6b7684',
            }}
          >
            <b style={{ color: '#0f2439' }}>Esta moto aún no tiene márgenes.</b>{' '}
            Cárgalos en <b>Márgenes motocicletas</b>; no necesita tener precio.
          </div>
        )}

        {sel && margenes.length > 0 && tieneReglas && (
          <div style={{ marginTop: 16, borderTop: '1px solid #e3e7ec', paddingTop: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#0f2439' }}>
              A cuánto queda facturada esta moto en cada clasificación
            </div>
            <div style={{ fontSize: 12, color: '#6b7684', margin: '4px 0 10px' }}>
              Simulación con el PVP tecleado, <b>sin guardar</b>. El margen se edita en <b>Márgenes motocicletas</b>.
            </div>
            {/* Mismas tarjetas que Back Comercial (`MargenTarjetas`). Acá van sin editar: Trade pone
                el PVP, el margen es política comercial y se administra en el tab Márgenes. */}
            <MargenTarjetas items={tarjetasMargen} />
            <div style={{ fontSize: 11.5, color: '#9aa5b1', marginTop: 8 }}>
              {chip(`${margenes.filter((l) => Number(l.regla_propia_id || 0) > 0).length} margen(es) propios de esta moto`, '#fff4dd', '#7a5300')}
              <span style={{ marginLeft: 8 }}>
                Los demás siguen la regla de la marca o la general.
              </span>
            </div>
          </div>
        )}

        <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="contained" color={todosSeleccionados ? 'primary' : 'warning'} onClick={guardar}>
            {colores.length === 0 || todosSeleccionados
              ? `Guardar precio ${sels.length > 1 ? `de ${sels.length} modelos` : 'del modelo'}${colores.length ? ` (${colores.length} productos)` : ''}`
              : `Guardar solo a ${seleccion.length} de ${colores.length} productos`}
          </Button>
          <Button variant="text" size="small" onClick={importar} style={{ color: '#6b7684' }}>
            Traer catálogo de SAP ahora
          </Button>
          <span style={{ fontSize: 11.5, color: '#9aa5b1', alignSelf: 'center' }}>
            (se actualiza solo a diario)
          </span>
        </div>
      </div>

      <div className="precios-card">
        <div className="precios-section-title">Modelos y su precio vigente ({grupos.length})</div>
        {error && (
          <div
            style={{
              background: '#fdecec',
              border: '1px solid #f3b6b6',
              color: '#8f2d2d',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 12.5,
              marginBottom: 12,
              display: 'flex',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <span>{error}</span>
            <Button size="small" variant="outlined" color="error" onClick={fetchGrupos}>
              Reintentar
            </Button>
          </div>
        )}
        <TablaPrecios columns={columns} data={grupos} isLoading={isLoading} pageSize={15} />
      </div>
    </div>
  )
}

export default TradePorModelo
